import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, notification } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import ProductService from "services/product";
import OrderService from "services/order";
import { useMutation } from "react-query";
import BasicButtonPrimary from "components/BasicButtonPrimary";
import { CardProductList, CardProductInfo, CardOrderSummary } from "./_components/OrderCards";

const CreateOrderPage = () => {
  const { t: translation } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedMap, setSelectedMap] = useState({});
  const [currentOrderData, setCurrentOrderData] = useState(null);

  // Điền thông tin khách hàng từ thanh toán thành công
  useEffect(() => {
    if (location.state?.customerData) {
      const { customerName, customerPhone, address, cccd } = location.state.customerData;
      const formData = {};
      if (customerName) formData.customerName = customerName;
      if (customerPhone) formData.customerPhone = customerPhone;
      if (address) formData.address = address;
      if (cccd) {
        if (/^\d{13}$/.test(cccd)) {
          formData.cccd = `${cccd.substring(0, 10)}-${cccd.substring(10)}`;
        } else {
          formData.cccd = cccd;
        }
      }
      
      if (Object.keys(formData).length > 0) {
        form.setFieldsValue(formData);
      }
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.state, form, location.pathname]);

  // Fetch products
  useEffect(() => {
    setLoadingProducts(true);
    const payload = { limit: 1000 };
    if (searchText) {
      payload.searchText = searchText;
    }
    ProductService.advanceUserGetList(payload)
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        const mapped = (list || []).map((p) => ({
          productId: p.productId ?? p.id,
          productName: p.productName ?? p.name,
          productPrice: p.price ?? p.productPrice ?? p.unitPrice ?? 0,
          productPriceAfterVAT: p.productPriceAfterVAT ?? p.productPrice ?? 0,
          productCategory: p.productCategory,
          productTax: p.productTax ?? 0,
        }));
        setProducts(mapped);
      })
      .finally(() => setLoadingProducts(false));
  }, [searchText]);

  const getProductId = (item) => item?.productId;
  const getProductName = (item) => item?.productName;
  const getProductImage = (item) => item?.productImages || null;
  const getProductPrice = (item) =>
    item?.price ?? item?.productPrice ?? item?.unitPrice ?? item?.sellPrice ?? 0;

  const toggleSelect = (item) => {
    const id = getProductId(item);
    if (!id) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = { quantity: 1, product: item, addedAt: Date.now() };
      return next;
    });
  };

  const changeQty = (item, value) => {
    const id = getProductId(item);
    if (!id) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      const val = Number(value || 0);
      const isSelected = !!next[id];
      
      if (!isSelected && val > 0) {
        next[id] = { quantity: Math.max(1, val), product: item, addedAt: Date.now() };
      } else if (isSelected) {
        if (val <= 0 || Number.isNaN(val)) {
          delete next[id];
        } else {
          next[id].quantity = Math.max(1, val);
        }
      }
      return next;
    });
  };

  const selectedProducts = useMemo(() => {
    return Object.entries(selectedMap)
      .map(([productId, data]) => ({
        productId,
        orderQuantity: data.quantity,
        product: data.product || products.find((p) => String(getProductId(p)) === String(productId)),
        addedAt: data.addedAt,
      }))
      .sort((a, b) => a.addedAt - b.addedAt);
  }, [selectedMap, products]);

  const subtotal = useMemo(() => {
    return selectedProducts.reduce((s, it) => {
      const price = getProductPrice(it.product) || 0;
      return s + price * (Number(it.orderQuantity) || 0);
    }, 0);
  }, [selectedProducts]);

  const tax = useMemo(() => {
    return selectedProducts.reduce((s, it) => {
      const price = getProductPrice(it.product) || 0;
      const priceAfterVAT = it.product?.productPriceAfterVAT ?? price;
      const taxAmount = priceAfterVAT - price;
      return s + taxAmount * (Number(it.orderQuantity) || 0);
    }, 0);
  }, [selectedProducts]);
  const fee = 0;
  const total = subtotal + tax + fee;

  const createOrderMutation = useMutation({
    mutationFn: (data) => OrderService.createOrder(data),
    onSuccess: (data) => {
      // API trả về [orderId] nếu success, hoặc object nếu error
      const isSuccess = Array.isArray(data) || data?.success || (data?.statusCode === 200 && data?.message === "Success");
      if (isSuccess) {
        notification['success']({
          message: "",
          description: translation("order.createSuccess")
        });
        const orderId = Array.isArray(data) ? data[0] : Array.isArray(data?.data) ? data.data[0] : data?.data?.orderId || data?.orderId;
        if (orderId) {
          localStorage.setItem('pendingOrderId', orderId);
          history.push('/orders?tab=1&payment=true');
        } else {
          notification['error']({
            message: "",
            description: "Không thể lấy ID đơn hàng"
          });
        }
      } else {
        notification['error']({
          message: "",
          description: data?.message || translation("order.createFailed")
        });
      }
    },
  });

  const onFinish = (values) => {
    if (!selectedProducts.length) {
      notification['error']({
        message: "",
        description: translation("orderMsg.selectProduct")
      });
      return;
    }
    const orderItemList = selectedProducts.map((it) => ({
      productId: it.productId,
      orderQuantity: it.orderQuantity,
    }));
    
    const userData = {
      firstName: values.customerName,
      phoneNumber: values.customerPhone,
    };
    if (!values.noInvoice) {
      if (values.address) {
        userData.userHomeAddress = values.address;
      }
      if (values.cccd) {
        userData.appUserIdentity = values.cccd;
      }
    }

    const orderData = {
      userData,
      orderItemList,
      paymentNote: `Thanh toan don hang`,
      orderType: 0,
    };
    
    setCurrentOrderData(orderData);
    createOrderMutation.mutate(orderData);
  };

  return (
    <div className="op-main-container">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={(errorInfo) => {
          const fieldLabelMap = {
            customerName: translation("order.customerName"),
            customerPhone: translation("order.customerPhone"),
            cccd: translation("order.taxIdOrCCCD"),
            address: translation("order.address"),
          };
          const msgs = (errorInfo.errorFields || []).map((f) => {
            const key = Array.isArray(f.name) ? f.name[0] : f.name;
            const label = fieldLabelMap[key] || key;
            return `${label}: ${f.errors.join(', ')}`;
          });
          if (msgs.length) {
            notification['error']({
              message: "",
              description: msgs.join('\n')
            });
          }
        }}
      >
        <div className="op-main-content">
          <Row gutter={24} align="stretch">
          <Col xs={24} md={24} lg={8} className="op-col-basic">
            <CardProductList
              products={products}
              loading={loadingProducts}
              searchText={searchText}
              setSearchText={setSearchText}
              selectedMap={selectedMap}
              toggleSelect={toggleSelect}
            />
          </Col>

          <Col xs={24} md={24} lg={8} className="op-col-flex">
            <CardProductInfo
              selectedProducts={selectedProducts}
              changeQty={changeQty}
              removeProduct={(p) => toggleSelect(p)}
            />
          </Col>

          <Col xs={24} md={24} lg={8} className="op-col-basic">
            <CardOrderSummary 
              selectedProducts={selectedProducts} 
              subtotal={subtotal} 
              tax={tax} 
              fee={fee} 
              total={total} 
              onCreateOrder={() => form.submit()} 
            />
          </Col>
          </Row>
        </div>
      </Form>
    </div>
  );
};

export default CreateOrderPage;